import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';

function readSingleRowMin(): number {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue('--fmt-toolbar-single-row-min')
    .trim();
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 443;
}

/**
 * Available inline size for layout decisions.
 *
 * The toolbar is `position: absolute` to the non-scrolling
 * `.pn-editor-shell-pane` (its DOM parent), which is the editor column and
 * EXCLUDES the chat rail. Reading `parentElement.clientWidth` returns the
 * pane's rendered width, which is the correct budget for the
 * compact-vs-single-row decision.
 */
function getAvailableInlineSize(el: HTMLElement): number {
  const parent = el.parentElement;
  if (parent) return parent.clientWidth;
  return el.getBoundingClientRect().width;
}

export type FormattingAction =
  | 'bold'
  | 'italic'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'bullet'
  | 'ordered'
  | 'quote'
  | 'code'
  | 'link'
  | 'divider';

export interface FormattingToolbarProps {
  /** When false the toolbar is not rendered (e.g. Preview mode). */
  visible: boolean;
  /** Fires the named action; the parent splices the markdown into a textarea. */
  onInsert: (action: FormattingAction) => void;
  className?: string;
  /** Inline style hook for stage-anchored positioning by the page. */
  style?: CSSProperties;
  /** Test prefix — buttons get `${dataTestId}-{action}`. */
  dataTestId?: string;
  /** When true, the button renders pressed (`aria-pressed`, `is-active`). */
  activeActions?: Partial<Record<FormattingAction, boolean>>;
}

const BUTTONS: Array<
  | {
      action: FormattingAction;
      label: string;
      title: string;
      content?: React.ReactNode;
    }
  | { sep: true }
> = [
  { action: 'bold', label: 'B', title: 'Bold', content: <strong>B</strong> },
  { action: 'italic', label: 'I', title: 'Italic', content: <em>I</em> },
  { sep: true },
  { action: 'h1', label: 'H1', title: 'Heading 1' },
  { action: 'h2', label: 'H2', title: 'Heading 2' },
  { action: 'h3', label: 'H3', title: 'Heading 3' },
  { sep: true },
  { action: 'bullet', label: '•', title: 'Bullet list' },
  { action: 'ordered', label: '1.', title: 'Ordered list' },
  { sep: true },
  { action: 'quote', label: '❝', title: 'Quote' },
  { action: 'code', label: '<>', title: 'Code block' },
  { action: 'link', label: '🔗', title: 'Link' },
  { action: 'divider', label: '—', title: 'Divider' }
];

/**
 * Floating toolbar with eleven markdown-insertion buttons. The primitive is
 * visual-only — `onInsert(action)` reports which action was clicked; the
 * parent (typically `MarkdownEditor` via its imperative `insertSyntax()` ref)
 * splices the corresponding markdown around the current selection in the host
 * textarea.
 *
 * Positioning is the page's responsibility (the page anchors it
 * `position: absolute` to the non-scrolling `.pn-editor-shell-pane`, pinned to
 * the pane bottom with the `--z-float-toolbar` token). Layout is responsive:
 * `data-compact` is set when available inline space (the pane width via
 * `parentElement.clientWidth`) is below `--fmt-toolbar-single-row-min`, so the
 * inner toolbar wraps to centered rows with token corners; otherwise controls
 * stay on one row in a pill.
 *
 * `onMouseDown` (not `onClick`) is used so the textarea doesn't lose focus
 * before the splice runs; we also call `e.preventDefault()` so the textarea
 * caret position is preserved.
 *
 * CSS namespace: `pn-fmt-toolbar-container`, `pn-fmt-toolbar` (-btn / -sep).
 */
export function FormattingToolbar({
  visible,
  onInsert,
  className,
  style,
  dataTestId,
  activeActions
}: FormattingToolbarProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [compact, setCompact] = useState(false);

  useLayoutEffect(() => {
    if (!visible) return;
    const el = containerRef.current;
    if (!el) return;

    let cancelled = false;
    const sync = () => {
      if (cancelled) return;
      const available = getAvailableInlineSize(el);
      setCompact(available < readSingleRowMin());
    };

    sync();
    requestAnimationFrame(() => {
      sync();
      requestAnimationFrame(sync);
    });

    window.addEventListener('resize', sync);
    const parent = el.parentElement;
    const ro = parent ? new ResizeObserver(sync) : null;
    if (parent && ro) ro.observe(parent);

    return () => {
      cancelled = true;
      window.removeEventListener('resize', sync);
      ro?.disconnect();
    };
  }, [visible]);

  if (!visible) return null;
  const containerCls = ['pn-fmt-toolbar-container', className]
    .filter(Boolean)
    .join(' ');
  const tid = (suffix: string) =>
    dataTestId ? `${dataTestId}-${suffix}` : undefined;

  return (
    <div
      ref={containerRef}
      className={containerCls}
      style={style}
      data-testid={dataTestId}
      data-compact={compact ? 'true' : 'false'}
    >
      <div className="pn-fmt-toolbar" role="toolbar" aria-label="Text formatting">
        {BUTTONS.map((b, i) => {
          if ('sep' in b) {
            return <span key={`sep-${i}`} className="pn-fmt-toolbar-sep" />;
          }
          return (
            <button
              key={b.action}
              type="button"
              className={[
                'pn-fmt-toolbar-btn',
                activeActions?.[b.action] ? 'is-active' : ''
              ]
                .filter(Boolean)
                .join(' ')}
              title={b.title}
              aria-label={b.title}
              aria-pressed={activeActions?.[b.action] ? true : false}
              data-testid={tid(b.action)}
              onMouseDown={(e) => {
                e.preventDefault();
                onInsert(b.action);
              }}
            >
              {b.content ?? b.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
